// src/features/quests/fromSystemMessage.js

import * as constants from "../../constants";

export function normalizeQuestActionsFromSystemMessage(response, userId) {
    const actions = [];
    if (!response) return actions;

    // Convert to string early
    let messageStr = typeof response === "string" ? response : JSON.stringify(response);

    const unwrapSocketIoPacketIfNeeded = (str) => {
        if (typeof str !== "string") return str;

        // Socket.IO event packet starts with 42
        if (!str.startsWith("42[")) return str;

        try {
            const decoded = JSON.parse(str.slice(2)); // remove leading "42"
            // decoded should be like: ["system_message", "<payload>"]
            if (Array.isArray(decoded) && decoded.length >= 2) {
                const eventName = decoded[0];
                const payload = decoded[1];

                if (eventName === "system_message") {
                    // payload is usually a string (often JSON array string)
                    return typeof payload === "string" ? payload : JSON.stringify(payload);
                }
            }
        } catch (e) {
            // ignore and fall back to original string
        }

        return str;
    };

    messageStr = unwrapSocketIoPacketIfNeeded(messageStr);

    const includes = (c) => messageStr.includes(c);

    const safeParse = () => {
        try {
            return JSON.parse(messageStr);
        } catch {
            return null;
        }
    };

    // Common events (KEEP EXACT BEHAVIOR)
    if (includes(constants.QUIZ_COMPLETE)) actions.push({ actionType: "QUIZ_COMPLETE" });
    if (includes(constants.NPC_OPEN)) actions.push({ actionType: "NPC_OPEN" });
    if (includes(constants.OPEN_GUESTBOOK)) actions.push({ actionType: "OPEN_GUESTBOOK" });
    if (includes(constants.OPEN_SPOTIFY)) actions.push({ actionType: "OPEN_SPOTIFY" });
    if (includes(constants.PDF_OPEN)) actions.push({ actionType: "PDF_OPEN" });
    if (includes(constants.OPEN_SMALL_IFRAME)) actions.push({ actionType: constants.OPEN_SMALL_IFRAME });
    if (includes(constants.EDIT_RPM_AVATAR)) actions.push({ actionType: constants.EDIT_RPM_AVATAR });
    if (includes(constants.OPEN_HALLWAY)) actions.push({ actionType: constants.OPEN_HALLWAY });
    if (includes(constants.OPEN_LINKOUT)) actions.push({ actionType: "OPEN_LINKOUT" });
    if (includes(constants.ENTERING_NAV_MENU)) actions.push({ actionType: constants.ENTERING_NAV_MENU });

    // NEW_ROOM_LOADING: ["NEW_ROOM_LOADING:<roomId>", "<userId>"]
    // (KEEP BEHAVIOR, but make parsing resilient)
    if (includes(constants.NEW_ROOM_LOADING)) {
        const parsed = safeParse();

        // If it's a JSON array, prefer that
        if (Array.isArray(parsed)) {
            const eventType = parsed?.[0];
            const receivedUser = parsed?.[1];
            const roomId = typeof eventType === "string" ? eventType.split(":")[1] : null;

            if (receivedUser === userId && roomId) {
                actions.push({ actionType: "NEW_ROOM_LOADING", roomId, payload: { rawEventType: eventType } });
            }
        } else {
            // Fallback: try to extract from raw string (rare)
            const match = messageStr.match(/NEW_ROOM_LOADING:([0-9a-fA-F-]{36})/);
            if (match?.[1]) {
                actions.push({
                    actionType: "NEW_ROOM_LOADING",
                    roomId: match[1],
                    payload: { rawEventType: `NEW_ROOM_LOADING:${match[1]}` },
                });
            }
        }
    }

    // AVATAR_ACTION parsing
    // Supports:
    // 1) Raw string: "AVATAR_ACTION:<userId>:<key>:<moveName...>"
    // 2) JSON array string: ["AVATAR_ACTION:<userId>:<key>:<moveName...>", "<userId>"]
    // 3) We only add action when it matches this userId and a key exists
    if (includes(constants.AVATAR_ACTION)) {
        const parsed = safeParse();

        let avatarMessage = null;
        let receivedUser = null;

        if (Array.isArray(parsed)) {
            avatarMessage = typeof parsed?.[0] === "string" ? parsed[0] : null;
            receivedUser = typeof parsed?.[1] === "string" ? parsed[1] : null;
        } else {
            avatarMessage = messageStr;
        }

        if (avatarMessage && avatarMessage.includes("AVATAR_ACTION:")) {
            const idx = avatarMessage.indexOf("AVATAR_ACTION:");
            const sliced = idx >= 0 ? avatarMessage.slice(idx) : avatarMessage;

            // AVATAR_ACTION:<userId>:<key>:<moveName...>
            const parts = sliced.split(":");
            const parsedUserId = parts?.[1] || receivedUser;
            const rawKey = parts?.[2];

            if (parsedUserId === userId && rawKey) {
                actions.push({
                    actionType: "AVATAR_ACTION",
                    payload: {
                        key: rawKey,
                        moveName: parts.slice(3).join(":") || "",
                    },
                });
            }
        }
    }

    return actions;
}
